import { useEffect, useState } from 'react';
import SvgIcon from '../SvgIcon';
import { colors } from '../../lib/colors';
import { formatAmountText, stripAmountComma } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';

type InputProps = {
    value: string | number;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    clearable?: boolean;
    onClear?: () => void;
    width?: number;
    height?: number;
    textInputHeight?: number;
    /**
     * DOM 의 maxLength 로 그대로 내려간다.
     * ⚠️ 콤마 모드(numeric && isMoney)와 함께 쓰면 **콤마가 글자 수를 잡아먹어**
     * 실제 입력 가능한 자릿수가 줄어든다(예: maxLength=7 → "1,234,5" 에서 막힘).
     * 금액에 자릿수 상한이 필요하면 maxLength 말고 호출부에서 값으로 검증해라.
     * (현재 콤마를 켜는 6곳에는 maxLength 가 없다 — 2026-09-09 확인)
     */
    maxLength?: number;
    isLeftIcon?: boolean;
    isRightIcon?: boolean;
    isClearIcon?: boolean;
    unit?: string;
    /**
     * 금액 표시용 3자리 콤마. **`numeric` 과 함께 켤 때만 동작한다.**
     *
     * 화면에만 콤마를 붙이고 onChange 로 나가는 문자열에는 콤마가 없다.
     * 호출부가 parseInt 로 받는데 parseInt('1,000') 은 에러 없이 1 이기 때문이다.
     * unit 이 '원' 인지로 자동 판별하지 않는다 — 회차 입력에 콤마가 새어 들어간다.
     */
    isMoney?: boolean;
    /**
     * 소수 입력 허용. **변경 전 동작을 그대로 잇기 위한 것이다.**
     * `numeric` 없이 일반 텍스트로 쓰던 호출부는 소수가 입력됐으므로 켜야 하고,
     * `/^\\d*$/` 로 정수만 받던 호출부는 꺼둬야 한다. 정수 칸을 열면
     * `parseInt('10.5')` 가 10 이라 화면과 payload 가 어긋난다.
     */
    allowDecimal?: boolean;
    isEnterChange?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    /**
     * 숫자 입력 모드. 켜면 입력 중에 사용자가 친 문자열을 그대로 들고 있는다.
     *
     * 부모가 보통 `value={n.toString()}` / `onChange={v => setN(parseInt(v))}` 로 쓰는데,
     * 그러면 입력할 때마다 값이 재구성되어 **커서가 끝으로 튀어** 가운데를 고칠 수 없다.
     * 켜면 숫자만 받고, "05" 같은 표기는 포커스가 빠질 때 정리한다.
     */
    numeric?: boolean;
};

const RadiusTextInput = ({
    value = '',
    onChange,
    placeholder = '값을 입력해주세요',
    disabled = false,
    error = false,
    clearable = false,
    onClear,
    width = 12,
    height = 36,
    textInputHeight = 36,
    maxLength,
    isLeftIcon,
    isRightIcon,
    isClearIcon = true,
    unit,
    isMoney = false,
    allowDecimal = false,
    isEnterChange = false,
    onFocus,
    onBlur,
    numeric = false,
}: InputProps) => {
    const [focus, setFocus] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    // 콤마는 숫자 모드에서만 붙인다. 표시(unit)로 판별하지 않는 명시적 opt-in 이다.
    const commaMode = numeric && isMoney;
    // 숫자 모드에서 화면에 그대로 유지할 입력 문자열
    const [numericText, setNumericText] = useState(() =>
        numeric && isMoney ? formatAmountText(String(value ?? '')) : String(value ?? ''),
    );
    /**
     * 거부 규칙과 커서 복원은 금액 입력 전체가 같은 것을 써야 한다.
     * 훅은 조건부로 못 부르므로 콤마 모드가 아니어도 항상 부르고, 쓸 것만 골라 쓴다.
     * 붙여넣기·드롭 차단(pasteAmount/dropAmount)은 **콤마 모드에서만** 연결한다 —
     * 검색창·회차 입력의 붙여넣기 동작까지 바꾸면 안 된다.
     */
    const { changeAmount, dropPendingCaret, pasteAmount, dropAmount } = useAmountInputCaret({ allowDecimal });
    useEffect(() => {
        if (!numeric) return;
        // 부모가 실제로 다른 수로 바꿨을 때만 동기화한다.
        setNumericText((current) => {
            if (commaMode) {
                // Number("1,000") 은 NaN 이다. 콤마를 걷어내고 비교하지 않으면
                // 부모가 값을 돌려주는 즉시 콤마가 사라진다.
                if (Number(stripAmountComma(current)) === Number(value)) return current;
                // 부모·서버가 값을 갈아끼웠다(rollback, 미수금액 재계산 등).
                // 사용자가 치던 자리의 낡은 커서 복원은 버린다.
                // ref 를 비우기만 하므로 updater 가 두 번 불려도 결과가 같다.
                dropPendingCaret();
                return formatAmountText(String(value ?? ''));
            }
            return Number(current) === Number(value) ? current : String(value ?? '');
        });
    }, [value, numeric, commaMode, dropPendingCaret]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (numeric) {
            const raw = e.target.value;
            if (commaMode) {
                // 거부(null)면 값을 아예 건드리지 않는다 — 걷어내는 방식(replace)은
                // 실패 대신 다른 유효 금액을 만든다 ("-100"→100, "12.34"→1234).
                // 커서 기억도 훅이 한다(자릿수가 그대로면 아예 기억하지 않는다).
                const digits = changeAmount(e, stripAmountComma(numericText));
                if (digits === null) return;
                setNumericText(formatAmountText(digits));
                // 부모는 parseInt 로 받는다. 콤마가 섞이면 parseInt('1,000') === 1 이 된다.
                onChange(digits);
                return;
            }
            // 숫자가 아니면 값을 바꾸지 않는다. 그래야 커서도 그대로 있는다.
            if (!/^\d*$/.test(raw)) return;
            setNumericText(raw);
            onChange(raw);
            return;
        }
        if (isEnterChange) {
            let newValue = e.target.value;

            // If there is a unit, remove it from the input value
            if (unit && newValue.endsWith(unit)) {
                newValue = newValue.slice(0, -unit.length);
            }

            setInputValue(newValue);
        } else {
            let newValue = e.target.value;

            onChange(e.target.value);
            setInputValue(newValue);
        }
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && isEnterChange) {
            onChange(inputValue as string);
        }
    };

    return (
        <div className={` relative h-[${textInputHeight}px] flex-row`} style={{ width: width, height: height }}>
            <input
                type="text"
                value={numeric ? numericText : disabled ? value : isEnterChange ? inputValue ?? '' : value}
                inputMode={numeric ? 'numeric' : undefined}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                /*
                 * 붙여넣기·드롭은 한 번에 값이 확정되므로 타이핑보다 엄격하게 본다.
                 * `10000,20000`(엑셀 CSV 조각)이 통과하면 콤마를 걷어낸 `1,000,020,000`
                 * 이라는 전혀 다른 금액이 만들어진다. 콤마 모드가 아닐 때는 아예 붙이지 않는다.
                 */
                onPaste={commaMode ? pasteAmount : undefined}
                onDrop={commaMode ? dropAmount : undefined}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
                aria-disabled={disabled}
                onFocus={() => {
                    setFocus(true);
                    onFocus && onFocus();
                }}
                onBlur={() => {
                    setFocus(false);
                    // 입력이 끝나면 "05" 같은 표기를 정리한다. 입력 중에 지우면 커서가 튄다.
                    if (numeric)
                        setNumericText(
                            commaMode
                                ? formatAmountText(String(Number(stripAmountComma(numericText)) || 0))
                                : String(Number(numericText) || 0),
                        );
                    onBlur && onBlur();
                }}
                className={`w-full h-full px-[16px] py-[6px] rounded-full border-[1px] focus:outline-none text-[16px] placeholder-GREY_200
          ${disabled ? 'bg-WHITE_500 text-LAVEL_1 cursor-not-allowed' : 'bg-white'}
          ${error ? 'border-red-500' : focus ? 'border-primary' : 'border-WHITE_600'}
          ${!disabled && !error ? 'hover:border-primary-dark' : ''}
          ${isLeftIcon ? 'pl-[44px]' : 'pl-[16px]'}
        `}
            />
            {value && isClearIcon && !disabled && (
                <div
                    className="absolute right-[16px] top-1/2 transform -translate-y-1/2 text-primary cursor-pointer"
                    onClick={() => {
                        setInputValue('');
                        onClear && onClear();
                    }}
                >
                    <SvgIcon name="SvgCircleX" size={18} fill={colors.WHITE_600} />
                </div>
            )}
            {isRightIcon && !value && !disabled && (
                <SvgIcon
                    name="SvgSearch"
                    size={12}
                    stroke={colors.GREY_200}
                    fill={colors.trans}
                    className="absolute right-[16px] top-1/2 transform -translate-y-1/2 w-[24px] h-[24px] text-primary cursor-pointer"
                />
            )}
            {isLeftIcon && !disabled && (
                <SvgIcon
                    name="SvgSearch"
                    size={12}
                    stroke={colors.GREY_200}
                    fill={colors.trans}
                    className="absolute left-[16px] top-1/2 transform -translate-y-1/2 w-[24px] h-[24px] text-primary cursor-pointer"
                />
            )}
        </div>
    );
};

export default RadiusTextInput;
